import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../api';
import { categoryEmoji } from '../categoryEmoji';
import { getCachedData, refreshCache } from '../store';
import { todayISO, formatDate, formatMoney } from '../dateUtils';
import { isIncludedAccount } from '../balance';
import TransactionForm from './TransactionForm';
import TypePicker from './TypePicker';
import Modal from './Modal';

export default function Transactions() {
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [txType, setTxType] = useState(null);
  const [editingTx, setEditingTx] = useState(null);
  // Сколько групп по дням показано (сначала текущий день, дальше более ранние)
  const [visibleCount, setVisibleCount] = useState(1);
  const sentinelRef = useRef(null);

  const openPicker = () => setShowPicker(true);
  const handleTypePick = (type) => {
    setShowPicker(false);
    setTxType(type);
    setShowForm(true);
  };
  const closeTxForm = () => {
    setShowForm(false);
    setTxType(null);
    setEditingTx(null);
  };

  // Текущий (актуальный) период: сегодня в диапазоне, иначе первый
  const pickCurrentPeriod = (list) => {
    if (!list || list.length === 0) return null;
    const today = todayISO();
    return list.find(p => today >= p.startDate && today <= p.endDate) || list[0];
  };

  const loadData = async () => {
    const periodsData = await api.getPeriods();
    setPeriods(periodsData);
    const cats = await api.getCategories();
    setCategories(cats);
    const accs = await api.getAccounts();
    setAccounts(accs);
    const period = pickCurrentPeriod(periodsData);
    if (period) {
      setSelectedPeriodId(period.id);
      await loadTx(period.id);
    } else {
      // Нет периодов — показываем все безпериодные операции (корректировки и т.п.)
      const all = await api.getTransactions();
      setTransactions(all);
    }
  };

  // Транзакции выбранного периода + безпериодные (корректировки, переводы, долги)
  const loadTx = async (periodId) => {
    const periodTx = await api.getTransactions({ periodId });
    const noPeriodTx = await api.getTransactions();
    setTransactions([...periodTx, ...noPeriodTx.filter(t => !t.periodId)]);
  };

  // Группируем транзакции по дням (сначала самые свежие, внутри дня — новые сверху)
  const groupedByDay = useMemo(() => {
    const map = {};
    (transactions || []).forEach(t => { (map[t.date] = map[t.date] || []).push(t); });
    return Object.keys(map).sort((a, b) => b.localeCompare(a)).map(date => ({
      date,
      items: map[date].slice().sort((a, b) => b.id - a.id),
    }));
  }, [transactions]);
  const visibleGroups = groupedByDay.slice(0, Math.min(visibleCount, groupedByDay.length));
  const loadMoreDays = () => setVisibleCount(c => Math.min(c + 1, groupedByDay.length));

  // Автоподгрузка предыдущих дней при прокрутке до конца
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || visibleCount >= groupedByDay.length) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMoreDays();
    }, { rootMargin: '120px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [visibleCount, groupedByDay.length]);

  useEffect(() => {
    // Мгновенно показываем историю из памяти браузера
    const cached = getCachedData();
    if (cached) {
      if (Array.isArray(cached.periods)) setPeriods(cached.periods);
      if (Array.isArray(cached.categories)) setCategories(cached.categories);
      if (Array.isArray(cached.accounts)) setAccounts(cached.accounts);
      const period = pickCurrentPeriod(cached.periods);
      if (period) {
        setSelectedPeriodId(period.id);
        setTransactions([
          ...(cached.transactions || []).filter(t => t.periodId === period.id),
          ...(cached.transactions || []).filter(t => !t.periodId),
        ]);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    // при смене периода показываем снова только текущий день
    setVisibleCount(1);
    if (selectedPeriodId) {
      loadTx(selectedPeriodId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriodId]);

  const handleDelete = async (id) => {
    if (window.confirm('Удалить транзакцию?')) {
      await api.deleteTransaction(id);
      await loadTx(selectedPeriodId);
      refreshCache();
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : '—';
  };

  const getAccountName = (accountId) => {
    const acc = accounts.find(a => a.id === accountId);
    return acc ? acc.name : '';
  };

  const getDebtName = (debtAccountId) => {
    const acc = accounts.find(a => a.id === debtAccountId);
    return acc ? (acc.person || acc.name) : '';
  };

  const txTitle = (tx) => {
    if (tx.type === 'transfer') return `Перевод: ${getAccountName(tx.accountId)} → ${getAccountName(tx.toAccountId)}`;
    if (tx.type === 'debt') {
      const labels = { lend: 'Дал в долг', borrow: 'Взял в долг', return: 'Вернул долг', collect: 'Получил обратно' };
      return `${labels[tx.debtKind] || 'Долг'}: ${getDebtName(tx.debtAccountId)}`;
    }
    return getCategoryName(tx.categoryId);
  };

  const txSign = (tx) => {
    if (tx.type === 'transfer') return '';
    if (tx.type === 'debt') {
      const isIn = tx.debtKind === 'borrow' || tx.debtKind === 'collect';
      return isIn ? '+' : '−';
    }
    return tx.type === 'expense' ? '−' : '+';
  };

  const txAmountClass = (tx) => {
    if (tx.type === 'transfer') return 'amount-income';
    if (tx.type === 'debt') {
      const isIn = tx.debtKind === 'borrow' || tx.debtKind === 'collect';
      return isIn ? 'amount-income' : 'amount-expense';
    }
    return tx.type === 'expense' ? 'amount-expense' : 'amount-income';
  };

  return (
    <div className="content">
      <h1>Транзакции</h1>
      <div className="form-group">
        <label>Период</label>
        <select value={selectedPeriodId} onChange={(e) => setSelectedPeriodId(e.target.value)}>
          {periods.map(p => (
            <option key={p.id} value={p.id}>{formatDate(p.startDate)} — {formatDate(p.endDate)}</option>
          ))}
        </select>
      </div>
      <button onClick={openPicker}>+ Добавить транзакцию</button>
      {showPicker && (
        <Modal title="Новая транзакция" onClose={() => setShowPicker(false)}>
          <TypePicker onPick={handleTypePick} onCancel={() => setShowPicker(false)} />
        </Modal>
      )}
      {showForm && (
        <TransactionForm
          transaction={editingTx}
          presetType={editingTx ? null : txType}
          categories={categories}
          periodId={selectedPeriodId}
          onSave={async (data) => {
            if (editingTx) {
              await api.updateTransaction(editingTx.id, data);
            } else {
              await api.createTransaction(data);
            }
            closeTxForm();
            await loadTx(selectedPeriodId);
            refreshCache();
          }}
          onCancel={closeTxForm}
        />
      )}
      <ul className="list">
        {visibleGroups.map(group => (
          <React.Fragment key={group.date}>
            <li className="list-day-header">
              {formatDate(group.date)}{group.date === todayISO() ? ' · сегодня' : ''}
            </li>
            {group.items.map(tx => (
              <li key={tx.id} className="list-item">
                <div className="list-item-left">
                  <span className="cat-icon">{tx.type === 'transfer' ? '↔' : tx.type === 'debt' ? '🤝' : categoryEmoji(getCategoryName(tx.categoryId))}</span>
                  <div className="tx-body">
                    <div className="list-item-title tx-title">{txTitle(tx)}</div>
                    <div className="tx-account-line">
                      <span className="tx-account">
                        <span className={`tx-account-dot${isIncludedAccount(accounts, tx.accountId) ? ' on' : ''}`} />
                        <span className="tx-account-name">{getAccountName(tx.accountId) || 'Без счёта'}</span>
                      </span>
                    </div>
                    <div className="tx-date">{formatDate(tx.date)}</div>
                    {tx.description ? <div className="tx-desc">{tx.description}</div> : null}
                  </div>
                </div>
                <span className={`amount ${txAmountClass(tx)}`}>
                  {txSign(tx)}{formatMoney(tx.amount)}
                </span>
                <div className="actions">
                  <button className="small-icon" onClick={() => { setEditingTx(tx); setShowForm(true); }}>✏️</button>
                  <button className="small-icon danger" onClick={() => handleDelete(tx.id)}>🗑️</button>
                </div>
              </li>
            ))}
          </React.Fragment>
        ))}
      </ul>
      {visibleCount < groupedByDay.length && (
        <div className="load-more-wrap" ref={sentinelRef}>
          <button className="secondary" onClick={loadMoreDays}>Показать более ранние дни</button>
        </div>
      )}
    </div>
  );
}
